import { Component } from "@theme/component";
import { DialogCloseEvent, showFocusRing } from "@theme/dialog";
import { ZoomMediaSelectedEvent } from "@theme/events";
import { scrollIntoView } from "@theme/scrolling";
import {
  viewTransitionsEligible,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
} from "@theme/utilities";

const VIEW_TRANSITION_NAME = "view-transition-name";
const GALLERY_MEDIA_FOCAL_POINT = "--gallery-media-focal-point";
const INSTANT = "instant";
const ARIA_CURRENT = "aria-current";

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ["dialog", "media", "thumbnails"];

  /** Whether the dialog was opened from a focused element, which focus should return to. */
  #returnsFocus = false;

  /**
   * Whether focus shows a ring, on the close button at open and on return after Escape: the dialog was opened from the
   * keyboard, or Tab used in it.
   */
  #ringOnEscape = false;

  #closingOnEscape = false;

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) {
      return;
    }

    [...thumbnails.querySelectorAll("button")].forEach((button, i) => {
      button.setAttribute(ARIA_CURRENT, `${i === activeIndex}`);
    });

    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener("scroll", this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener("scroll", this.handleScroll);
    this.handleScroll.cancel();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {Event} event - The activation event.
   */
  async open(index, event) {
    event.preventDefault();

    const { activeElement } = document;
    const opener = activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null;
    this.#returnsFocus = opener !== null;
    this.#ringOnEscape = opener?.matches(":focus-visible") ?? false;

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      // The browser focuses the close button, and draws its ring whenever nothing held focus, as after a click on the
      // carousel. Like the site's other pop-ups, the ring stays only for a visitor on the keyboard.
      showFocusRing(this.#ringOnEscape);

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: INSTANT });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest("li,slideshow-slide") : null;

    if (!viewTransitionsEligible() || !sourceImage || !targetImage) {
      open();
      this.selectThumbnail(index, { behavior: INSTANT });
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty(VIEW_TRANSITION_NAME, itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty(GALLERY_MEDIA_FOCAL_POINT, focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty(VIEW_TRANSITION_NAME);
      targetImage.style.setProperty(VIEW_TRANSITION_NAME, itemTransitionName);
    }, ["gallery-zoom"]);

    document.documentElement.style.removeProperty(GALLERY_MEDIA_FOCAL_POINT);
    targetImage.style.removeProperty(VIEW_TRANSITION_NAME);

    this.selectThumbnail(index, { behavior: INSTANT });
  }

  /**
   * Opens the zoom dialog from a focusable carousel image using standard button keys.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  openOnKeyDown(index, event) {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    this.open(index, event);
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!viewTransitionsEligible()) {
      this.closeDialog();
      return;
    }

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest("media-gallery")
    );

    const slideshowActive = mediaGallery?.presentation === "carousel";

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      this.closeDialog();
      return;
    }
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty(GALLERY_MEDIA_FOCAL_POINT, focalPoint);
    }

    dialog.classList.add("dialog--closed");

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty(VIEW_TRANSITION_NAME, itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty(VIEW_TRANSITION_NAME);
      slide.style.setProperty(VIEW_TRANSITION_NAME, itemTransitionName);
      this.closeDialog();
    }, ["gallery-zoom"]);

    slide.style.removeProperty(VIEW_TRANSITION_NAME);
    dialog.classList.remove("dialog--closed");
    document.documentElement.style.removeProperty(GALLERY_MEDIA_FOCAL_POINT);
  }

  /**
   * Closes the zoom when the empty space around a photo fitted to the screen is clicked, as clicking the photo does.
   * The listener sits on the list of media, so a click inside a slide that has no handler of its own (a video) must
   * not close it.
   *
   * @param {Event} event - The click event; the component layer retargets `target` to the list.
   */
  closeOnBackdropClick(event) {
    if (event.composedPath()[0] === event.target) {
      this.close();
    }
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    this.#restoreFocus();
    globalThis.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * After Escape, focus returns with a ring only for keyboard use, like the site's other pop-ups. Stepping through the
   * zoom moves the carousel behind it, which makes the opening slide inert, so the dialog's own focus return fails;
   * focus then goes to the slide the carousel now shows.
   */
  #restoreFocus() {
    const closingOnEscape = this.#closingOnEscape;
    this.#closingOnEscape = false;
    if (!this.#returnsFocus) {
      return;
    }

    const active = document.activeElement;
    if (active && active !== document.body && !this.refs.dialog.contains(active)) {
      if (closingOnEscape) {
        showFocusRing(this.#ringOnEscape);
      }
      return;
    }

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.closest("media-gallery"));
    const slideshow = mediaGallery?.slideshow;
    slideshow?.slides?.[slideshow.current]?.focus({
      preventScroll: true,
      ...(closingOnEscape && { focusVisible: this.#ringOnEscape }),
    });
  }

  /**
   * Escape closes the dialog; Left/Right arrows step to the previous/next media, matching
   * the carousel behind the dialog. Up/Down stay native so a zoomed image still scrolls.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.#closingOnEscape = true;
      this.close();
      return;
    }
    if (event.key === "Tab") {
      this.#ringOnEscape = true;
    }
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      this.#stepMedia(event.key === "ArrowRight" ? 1 : -1, event);
    }
  }

  /**
   * Selects the thumbnail `delta` steps away from the current one, clamped to the gallery.
   * @param {number} delta
   * @param {KeyboardEvent} event
   */
  #stepMedia(delta, event) {
    const buttons = [...(this.refs.thumbnails?.querySelectorAll("button") ?? [])];
    if (buttons.length === 0) {
      return;
    }
    const current = buttons.findIndex(button => button.getAttribute(ARIA_CURRENT) === "true");
    const index = Math.max(0, current) + delta;
    if (index < 0 || index >= buttons.length) {
      return;
    }
    event.preventDefault();
    this.handleThumbnailClick(index);
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? INSTANT : "smooth";
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} [options] - The options for the selection.
   * @param {ScrollBehavior} [options.behavior] - The behavior of the scroll.
   */
  selectThumbnail(index, options) {
    const behavior = options?.behavior || "smooth";
    if (!this.refs.thumbnails || this.refs.thumbnails.children.length === 0) {
      return;
    }

    // Guard if invalid
    if (Number.isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) {
      return;
    }

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) {
      return;
    }

    [...thumbnails.querySelectorAll("button")].forEach((button, i) => {
      button.setAttribute(ARIA_CURRENT, `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      behavior,
      ancestor: thumbnails,
      block: "center",
      inline: "center",
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior,
      });
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get("zoom-dialog")) {
  customElements.define("zoom-dialog", ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise(resolve => {
    const observer = new IntersectionObserver(
      entries => {
        let mostVisible = entries[0];
        for (let i = 1; i < entries.length; i++) {
          const entry = entries[i];
          if (mostVisible && entry && entry.intersectionRatio > mostVisible.intersectionRatio) {
            mostVisible = entry;
          }
        }
        observer.disconnect();
        if (mostVisible) {
          resolve(/** @type {HTMLElement} */ (mostVisible.target));
        }
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      },
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
